import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { Editor } from 'primereact/editor';

// Components
import DashboardLayout from "./components/Layout";
// Api
import { getEventByIdAdmin, updateEventByIdAdmin } from "../../api/plan";
import { getPaymentModes } from "../../api/payment-mode";

export function dateTimeToInputValue(dateString) {
  const date = new Date(dateString);

  // Convert to local time
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);

  return local.toISOString().slice(0, 16); // "YYYY-MM-DDTHH:MM"
}

const SettingsPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [paymentModes, setPaymentModes] = React.useState([]);

  const {
    register,
    handleSubmit,
    setValue,
    control,
    formState: { isSubmitting },
  } = useForm();

  useEffect(() => {
    getEventByIdAdmin(eventId).then((res) => {
      const data = res?.data?.data;
      if (data) {
        setValue("title", data.title);
        setValue("description", data.description);
        setValue("place", data.place);
        setValue("date", data.date?.split("T")[0]);
        setValue("time", data.date?.split("T")[1]?.slice(0, 5));
        setValue("endDate", data.endDate?.split("T")[0]);
        setValue("endTime", data.endDate?.split("T")[1]?.slice(0, 5));
        setValue("ticketCCEmail", data.ticketCCEmail);
        setValue("supportPhoneNumber", data.supportPhoneNumber);
        setValue("publicKey", data.publicKey);
        setValue("privateKey", data.privateKey);
        setValue("isActive", data.isActive);
        setValue("paymentModeIds", data.paymentModeIds);
      }
    });
  }, [setValue, eventId]);

  useEffect(() => {
    getPaymentModes().then((res) => {
      const paymentModes = res?.data?.items || [];
      setPaymentModes(paymentModes);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = async (formData) => {
    const payload = {
      title: formData.title,
      description: formData.description,
      place: formData.place,
      date: formData.date + "T" + formData?.time + ":00",
      endDate: formData.endDate + "T" + formData?.endTime + ":00",
      ticketCCEmail: formData.ticketCCEmail,
      supportPhoneNumber: formData.supportPhoneNumber,
      publicKey: formData.publicKey,
      privateKey: formData.privateKey,
      isActive: formData.isActive,
      paymentModeIds: formData.paymentModeIds || [],
    };

  
    await updateEventByIdAdmin(eventId, payload);

    navigate(`/dashboard/${eventId}`);
  };

  return (
    <DashboardLayout id={eventId}>
      <div className="max-w-2xl mx-auto mt-10 bg-white p-8 rounded-xl shadow">
        <h2 className="text-2xl font-bold mb-6">
          ⚙️ Parametrlər
        </h2>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">Ad</label>
            <input
              {...register("title", { required: true })}
              className="mt-1 w-full border border-gray-300 p-2 rounded"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Açıqlama</label>

            <Controller
              name="description"
              control={control}
              defaultValue=""
              rules={{ required: "Description is required" }}
              render={({ field }) => (
                <Editor
                  value={field.value}
                  onTextChange={(e) => field.onChange(e.htmlValue)}
                  style={{ height: '320px' }} 
                />
                )}
              />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Dəstək üçün Telefon</label>
            <input
              {...register("supportPhoneNumber", { required: true })}
              className="mt-1 w-full border border-gray-300 p-2 rounded"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Məkan</label>
            <input
              {...register("place", { required: true })}
              className="mt-1 w-full border border-gray-300 p-2 rounded"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Tarix</label>
            <div className="flex space-x-2">
              <input
                type="date"
                {...register("date", { required: true })}
                className="mt-1 flex-[2] border border-gray-300 p-2 rounded"
              />
              <input
                type="time"
                {...register("time", { required: true })}
                className="mt-1 flex-[1] border border-gray-300 p-2 rounded"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Son Tarix</label>
            <div className="flex space-x-2">
              <input
                type="date"
                {...register("endDate", { required: false })}
                className="mt-1 flex-[2] border border-gray-300 p-2 rounded"
              />
              <input
                type="time"
                {...register("endTime", { required: false })}
                className="mt-1 flex-[1] border border-gray-300 p-2 rounded"
              />
            </div>
          </div>

          <h3 className="text-lg font-semibold mt-6">Ödəmə növləri</h3>
              
          {paymentModes?.map(paymentMode => <div className="flex items-center">
            <input
              type="checkbox"
              value={paymentMode._id}
              {...register("paymentModeIds[]")}
              className="mr-2"
            />
            <label className="text-sm text-gray-700">{paymentMode?.name?.az}</label>
          </div>)}


          <h3 className="text-lg font-semibold mt-6">API Parametrləri</h3>

          <div>
            <label className="block text-sm font-medium text-gray-700">Email (bilet kopyası üçün)</label>
            <input
              type="email"
              {...register("ticketCCEmail", { required: false })}
              className="mt-1 w-full border border-gray-300 p-2 rounded"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Public Key</label>
            <input
              {...register("publicKey", { required: false })}
              className="mt-1 w-full border border-gray-300 p-2 rounded"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Private Key</label>
            <input
              {...register("privateKey", { required: false })}
              className="mt-1 w-full border border-gray-300 p-2 rounded"
            />
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              {...register("isActive")}
              className="mr-2"
            />
            <label className="text-sm text-gray-700">Aktivdir</label>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="bg-black text-white px-4 py-2 rounded hover:bg-gray-800"
          >
            Yadda saxla
          </button>
        </form>
      </div>
    </DashboardLayout>
  );
};

export default SettingsPage;
